import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import { GOOGLE } from '@rsos/constants/mapProviders';
import { MapsNotReadyError } from '@rsos/maps/interface/reverseGeocode/googleGeocoderReady';
import { loadGoogleMaps } from '@rsos/maps/providers/google/loadGoogleMaps';
import { usePSAPCountryAlpha2 } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import type { StateWithSinatra } from '@rsos/sinatra/src/types/SinatraState';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import type { GoogleMapOptions } from './types';

interface UseGoogleMapParams {
  /** Google maps API key */
  apiKey?: string;
  /** Google maps ID for applying custom styling (e.g., light or dark mode). */
  mapId?: string;
  /** Google maps options */
  options?: google.maps.MapOptions | null;
}

interface MapState {
  mapInstance: google.maps.Map | null;
  initialized: boolean;
}

/**
 * Handle loading google maps if window.google is undefined. Returns the Google
 * maps instance, a ref, and an initialized flag for use in other components.
 */
const useGoogleMap = ({
  apiKey = '',
  mapId = '',
  options = null,
}: UseGoogleMapParams) => {
  const [mapState, setMapState] = useState<MapState>({
    mapInstance: null,
    initialized: false,
  });

  const mapRef = useRef<HTMLDivElement>(null);

  const basemap = useSelector(
    (state: StateWithSinatra) => state?.sinatra?.ui?.basemap,
  );

  const isGoogleBasemap = useMemo(() => {
    return (
      basemap === BASEMAP_GOOGLE_HYBRID || basemap === BASEMAP_GOOGLE_ROADMAP
    );
  }, [basemap]);

  // Google resolves country-specific place names from this (Lake Ontario vs
  // Lake America). Fixed on the loader URL, so only the first caller's value
  // applies and it cannot be a dep of the effect below.
  const region = usePSAPCountryAlpha2();

  useEffect(() => {
    // Each mount owns its own resolution. Sharing a callback through `window`
    // meant a second mount clobbered the first, which then never initialized.
    let cancelled = false;

    const initMap = (maps: typeof google.maps) => {
      if (cancelled || !mapRef.current) return;

      const mapInstance = new maps.Map(mapRef.current, {
        ...options,
        ...(mapId && {
          mapId,
          renderingType: maps.RenderingType?.VECTOR, // Use vector rendering type so that hybrid basemap labels are visible.
        }),
        mapTypeControl: options?.mapTypeControl || false,
        mapTypeId: options?.mapTypeId
          ? options.mapTypeId
          : isGoogleBasemap
            ? basemap
            : maps.MapTypeId?.ROADMAP,
      });

      setMapState({ mapInstance, initialized: true });

      exportToGlobalNamespace('map', {
        mapProvider: GOOGLE,
        mapInstance,
      } satisfies GoogleMapOptions);
    };

    // 'marker' as well as 'maps', because marker creation reads
    // maps.marker.AdvancedMarkerElement synchronously and silently falls back
    // to the deprecated Marker when it is not registered yet.
    loadGoogleMaps(['maps', 'marker'], { apiKey, region })
      .then(initMap)
      .catch(error => {
        if (cancelled) return;
        if (error instanceof MapsNotReadyError) {
          // Expected when the API cannot load at all (no key, offline). Warn
          // rather than error so Sentry's console integration stays quiet.
          // eslint-disable-next-line no-console
          console.warn(error.message);
          return;
        }
        captureExceptionWithScope(error as Error);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId]);

  return { mapRef, ...mapState };
};

export default useGoogleMap;
